import {
  Chart,
  LineController,
  BarController,
  DoughnutController,
  PolarAreaController,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  Legend,
  Tooltip,
  Filler,
} from 'chart.js';
import type { ChartSpec, Locale, SeriesValue } from '../types';

Chart.register(
  LineController,
  BarController,
  DoughnutController,
  PolarAreaController,
  LineElement,
  PointElement,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  Legend,
  Tooltip,
  Filler,
);

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

const is2D = (v: SeriesValue): v is number[][] => Array.isArray(v[0]);
const withAlpha = (hex: string, alpha: string) => `${hex}${alpha}`;

/** Builds a Chart.js config object from a declarative ChartSpec + numeric series. */
export function buildChart(spec: ChartSpec, series: SeriesValue, locale: Locale): Chart {
  const canvas = document.getElementById(`chart-${spec.id}`) as HTMLCanvasElement | null;
  if (!canvas) throw new Error(`Canvas not found for chart ${spec.id}`);

  Chart.defaults.font.family = locale === 'ar' ? 'IBM Plex Sans Arabic' : 'IBM Plex Sans';
  Chart.defaults.font.size = 12;
  Chart.defaults.color = cssVar('--muted') || '#6b7a92';

  const grid = cssVar('--grid') || 'rgba(20,40,80,.08)';
  const legend = { position: 'bottom' as const, labels: { usePointStyle: true, padding: 14 } };
  const pctTicks = spec.percent ? { callback: (v: string | number) => `${v}%` } : {};

  if (spec.type === 'line' || spec.type === 'bar') {
    const rows = is2D(series) ? series : [series as number[]];
    const datasets = rows.map((data, i) => {
      const color = spec.colors[i] ?? cssVar('--brand');
      return spec.type === 'line'
        ? {
            label: spec.datasetLabels?.[i] ?? '',
            data,
            borderColor: color,
            backgroundColor: spec.fill && i === 0 ? withAlpha(color, '1f') : 'transparent',
            tension: 0.35,
            fill: Boolean(spec.fill && i === 0),
            borderWidth: 2.5,
            pointRadius: 3,
          }
        : {
            label: spec.datasetLabels?.[i] ?? '',
            data,
            backgroundColor: color,
            borderRadius: 6,
            barPercentage: 0.7,
          };
    });

    return new Chart(canvas, {
      type: spec.type,
      data: { labels: spec.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend },
        scales: {
          y: { grid: { color: grid }, ticks: pctTicks, min: spec.min },
          x: { grid: { display: spec.type === 'bar' ? false : true, color: grid } },
        },
      },
    });
  }

  // doughnut / polarArea (single-series)
  const data = (is2D(series) ? series[0] : series) as number[];
  return new Chart(canvas, {
    type: spec.type,
    data: { labels: spec.labels, datasets: [{ data, backgroundColor: spec.colors, borderWidth: 0 }] },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: spec.type === 'doughnut' ? '60%' : undefined,
      plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 10, font: { size: 10.5 } } } },
      scales: spec.type === 'polarArea' ? { r: { grid: { color: grid }, ticks: { display: false } } } : {},
    },
  });
}
